import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../../api/apiClient';

export interface LocationSummaryRequest {
  region?: string[];
  market?: string[];
  locationId?: string[];
  locType?: string[];
  startDate?: string | null;
  endDate?: string | null;
  year?: number[];
  period?: number[];
  week?: number[];
  page?: number;
  size?: number;
  grainColumns?: string[];
  sort?: string | null;
}

export interface LocationSummaryDataItem {
  grainKey: Record<string, string | null | undefined>;
  planned: number;
  completed: number;
  completedPercentage: number;
  incomplete: number;
  onTime: number;
  onTimePercentage: number;
  driverArrivedAsExpected: number;
  driverManifestCount: number;
  driverArrivedPercentage: number;
}

export interface LocationSummaryDataContent {
  content: LocationSummaryDataItem[];
  totals?: LocationSummaryDataItem | null;
  totalElements: number;
  totalPages: number;
  pageNumber: number;
  pageSize: number;
  executionTimeMs: number;
  asOfDate: string;
}

export interface LocationSummaryResponse {
  data: LocationSummaryDataContent;
  executionTimeMs: number;
  asOfDate: string;
  isApproximate: boolean;
  message: string | null;
}

export interface AppliedFiltersForLocationSummary {
  selectedRegions: { id: string; displayText: string }[];
  selectedMarkets: { id: string; displayText: string }[];
  selectedLocations: { id: string; displayText: string }[];
  selectedLocationTypes: { id: string; displayText: string }[];
  selectedYears: { id: string; displayText: string }[];
  selectedPeriods: { id: string; displayText: string }[];
  selectedWeeks: { id: string; displayText: string }[];
  deliveryDateRange: { start: string | null; end: string | null };
}

const toIdArray = (items?: { id: string | number; displayText: string }[]): string[] | undefined => {
  if (!items || items.length === 0) return undefined;
  const ids = items.map((item) => String(item.id).trim()).filter(Boolean);
  return ids.length > 0 ? ids : undefined;
};

export function buildLocationSummaryPayload(
  filters: AppliedFiltersForLocationSummary,
  adminLocations: any[], // kept for call-site compatibility; backend now resolves region/market/location itself
  page: number = 1,
  size: number = 25,
  grainColumns: string[] = [],
  sortBy?: string,
  sortDirection?: 'asc' | 'desc'
): LocationSummaryRequest {
  const region = toIdArray(filters.selectedRegions);
  const market = toIdArray(filters.selectedMarkets);
  const locationId = toIdArray(filters.selectedLocations);
  const locType = toIdArray(filters.selectedLocationTypes);

  const years = filters.selectedYears.map((y) => Number(y.id)).filter((n) => Number.isFinite(n));
  const periods = filters.selectedPeriods
    .map((p) => {
      const raw = String(p.id).replace(/^P/i, '');
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    })
    .filter((n): n is number => n !== null);
  const weeks = filters.selectedWeeks.map((w) => Number(w.id)).filter((n) => Number.isFinite(n));

  // ✅ FIXED: Use fixed grain columns as specified in API requirements
  // Always send these specific grain columns: ["region","market","locationId","locationName","locType","year","period"]
  const FIXED_GRAIN_COLUMNS = ['region', 'market', 'locationId', 'locationName', 'locType', 'year', 'period', 'week'];

  // ✅ Map sortBy field names (e.g., 'week' → 'weekInYear', 'daeN' → 'driverArrivedAsExpected', 'locationType' → 'locType')
  const mappedSortField = sortBy ? (
    sortBy === 'week' ? 'weekInYear' :
    sortBy === 'daeN' ? 'driverArrivedAsExpected' :
    sortBy === 'daePct' ? 'driverArrivedPercentage' :
    sortBy === 'locationType' ? 'locType' :
    sortBy
  ) : undefined;
  const sort = mappedSortField
    ? `${mappedSortField}:${(sortDirection || 'asc').toUpperCase()}`
    : null;

  const payload: LocationSummaryRequest = {
    ...(region && { region }),
    ...(market && { market }),
    ...(locationId && { locationId }),
    ...(locType && { locType }),
    startDate: filters.deliveryDateRange.start ?? null,
    endDate: filters.deliveryDateRange.end ?? null,
    ...(years.length > 0 && { year: years }),
    ...(periods.length > 0 && { period: periods }),
    ...(weeks.length > 0 && { week: weeks }),
    grainColumns: FIXED_GRAIN_COLUMNS,
    sort,
    page: Math.max(1, page),
    size,
  };

  console.log('[LocationSummary] Payload:', payload);
  return payload;
}

export async function fetchLocationSummary(request: LocationSummaryRequest): Promise<LocationSummaryResponse> {
  const api = createApiClient();
  console.log('[LocationSummary API] Request:', JSON.stringify(request, null, 2));
  try {
    const clientTimestamp = getLocalTimestamp();
    const clientTimezone = getTimezoneOffsetString();
    const response = await api.post<LocationSummaryResponse>(
      '/ontime-dispatch/location-summary',
      request,
      { params: { clientTimestamp, clientTimezone } }
    );
    console.log('[LocationSummary API] Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[LocationSummary API] Error:', error);
    throw error;
  }
}
